/**
 * InterestSignalComposer.js — RC 1.30.G.
 *
 * Dialog that lets an employer send a structured interest signal
 * against a single anonymous match. Fetches the /prefill on open,
 * enforces a 600-char client-side limit with a live counter, and
 * posts to /signal. Success → parent bumps `refreshKey` so the
 * card's sent state updates immediately.
 *
 * No candidate identity is ever loaded or shown.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';

const MAX_LEN = 600;

export default function InterestSignalComposer({
  roleId, match, matchLabel, open, onOpenChange, onSent,
}) {
  const [body, setBody]       = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset + prefill on open.
  useEffect(() => {
    if (!open || !roleId || !match?.id) return;
    let cancelled = false;
    setLoading(true);
    setBody('');
    (async () => {
      try {
        const data = await apiFetch(
          `/api/employer/roles/${roleId}/matches/${match.id}/signal/prefill`,
        );
        if (!cancelled) setBody(data?.prefill || '');
      } catch {
        if (!cancelled) setBody('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, roleId, match?.id]);

  const trimmedLen = body.trim().length;
  const overLimit  = trimmedLen > MAX_LEN;
  const canSend    = !sending && trimmedLen > 0 && !overLimit;

  const onSend = async () => {
    if (!canSend || !match?.id) return;
    setSending(true);
    try {
      const resp = await apiFetch(
        `/api/employer/roles/${roleId}/matches/${match.id}/signal`,
        {
          method: 'POST',
          body: JSON.stringify({ signal_body: body.trim() }),
        },
      );
      if (resp?.already_sent) {
        toast.info(
          matchLabel
            ? `A pending signal already exists · ${matchLabel}.`
            : 'A pending signal already exists for this match.',
        );
      } else {
        toast.success(
          matchLabel
            ? `Signal sent · ${matchLabel}`
            : 'Signal sent. Their identity remains private.',
        );
      }
      if (onSent) onSent(resp?.signal || null);
      onOpenChange(false);
    } catch (err) {
      // Keep the composer open on failure so the employer can retry
      // without losing the drafted body.
      toast.error(err?.data?.detail || err?.message || 'Could not send signal.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl bg-ndor-surface text-ndor-ink border border-ndor-edge"
        data-testid="interest-signal-composer"
      >
        <DialogHeader>
          <DialogTitle>Express interest</DialogTitle>
          <DialogDescription>
            The candidate will see this signal and your role details.
            Their identity remains private until they choose to accept.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {matchLabel && (
            <div className="text-[11px] uppercase tracking-wider text-ndor-ink-muted">
              Signalling for: {matchLabel}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-ndor-ink-muted py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing suggested text…
            </div>
          ) : (
            <>
              <p className="text-[11px] text-ndor-ink-muted italic">
                Pre-filled from match analysis. Edit freely.
              </p>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={9}
                className="resize-none text-sm bg-ndor-bg text-ndor-ink placeholder:text-ndor-ink-muted border-ndor-edge focus-visible:ring-1 focus-visible:ring-ndor-glow"
                placeholder="Introduce your interest in a few sentences…"
                data-testid="signal-body-input"
              />
            </>
          )}

          <div className="flex items-center justify-between text-[11px]">
            <span
              className={
                overLimit ? 'text-rose-500' : 'text-ndor-ink-muted'
              }
              data-testid="signal-body-counter"
            >
              {trimmedLen}/{MAX_LEN}
              {overLimit && ' — over limit'}
            </span>
            <span className="text-ndor-ink-muted">
              No contact details are exchanged until the candidate
              accepts this signal.
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-ndor-edge">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="signal-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSend}
            disabled={!canSend}
            data-testid="signal-send"
            className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
          >
            {sending
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <Send className="h-4 w-4 mr-1.5" />}
            {sending ? 'Sending…' : 'Send signal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
